import { Pressable } from "react-native";
import { View, Text } from "@src/components";
import type { TextProps } from "@src/components";
import { Icon } from "@src/components/Icon";
import { useUnistyles } from "react-native-unistyles";
import { memo, type FC, type ReactNode } from "react";
import { Skeleton } from "@visioni/a3s-ui/components";

export { CircularIcon, type CircularIconProps } from "@visioni/a3s-ui/components";
export { SimpleIcon, type SimpleIconProps } from "@visioni/a3s-ui/components";

interface StatusRowProps {
  icon: ReactNode;
  title: string;
  subtitle?: string;
  titleTextStyle?: TextProps["textStyle"];
  subtitleTextStyle?: TextProps["textStyle"];
  alignTextWithIcon?: "auto" | "center";
  rightContent?: ReactNode;
  bottomContent?: ReactNode;
  onPress?: () => void;
  disabled?: boolean;
}

export const StatusRow: FC<StatusRowProps> = memo(
  ({
    icon,
    title,
    subtitle,
    titleTextStyle = "sm/regular",
    subtitleTextStyle = "xxs/regular",
    alignTextWithIcon = "auto",
    rightContent,
    bottomContent,
    onPress,
    disabled = false,
  }) => {
    const { theme } = useUnistyles();
    const hasSubtitle = !!subtitle;
    const shouldCenterAlign = alignTextWithIcon === "center";

    const content = (
      <View bg="surfaceCardInner" borderRadius={theme.spacing.lg} borderWidth={1} borderColor="borderCard">
        <View
          flexDirection="row"
          alignItems={hasSubtitle ? "flex-start" : "center"}
          justifyContent="space-between"
          minHeight={theme.spacing["12xl"]}
          px="3xl"
          py="lg"
          gap="xl"
        >
          <View
            flex={1}
            flexDirection="row"
            alignItems={
              shouldCenterAlign ? "center"
              : hasSubtitle ?
                "flex-start"
              : "center"
            }
            gap="xl"
            style={{ minWidth: 0 }}
          >
            <View
              width={theme.spacing["8xl"]}
              height={theme.spacing["8xl"]}
              alignItems="center"
              justifyContent="center"
            >
              {icon}
            </View>
            <View
              flex={1}
              style={{
                minWidth: 0,
                justifyContent: shouldCenterAlign ? "center" : "flex-start",
                minHeight: shouldCenterAlign ? theme.spacing["8xl"] : undefined,
              }}
            >
              <Text textStyle={titleTextStyle} color="text">
                {title}
              </Text>
              {subtitle && (
                <Text textStyle={subtitleTextStyle} color="textMuted">
                  {subtitle}
                </Text>
              )}
            </View>
          </View>
          {rightContent}
        </View>
        {bottomContent && (
          <View px="3xl" pb="lg">
            {bottomContent}
          </View>
        )}
      </View>
    );

    if (onPress) {
      return (
        <Pressable
          onPress={onPress}
          disabled={disabled}
          style={{ borderRadius: theme.spacing.lg, opacity: disabled ? 0.55 : 1 }}
        >
          {content}
        </Pressable>
      );
    }
    return content;
  },
);

interface NavigationRowRightContentProps {
  value: string;
  showChevron?: boolean;
  /** Shows a skeleton sized to `value` in place of the text. */
  isLoading?: boolean;
}

export const NavigationRowRightContent: FC<NavigationRowRightContentProps> = memo(
  ({ value, showChevron = true, isLoading = false }) => {
    const { theme } = useUnistyles();
    const text = (
      <Text textStyle="xs/regular" color="textMuted">
        {value}
      </Text>
    );
    return (
      <View flexDirection="row" alignItems="center" gap="lg">
        {isLoading ?
          <Skeleton textStyle="xs/regular">{text}</Skeleton>
        : text}
        {showChevron && <Icon name="chevronRight" size={theme.spacing["6xl"]} color={theme.colors.textMuted} />}
      </View>
    );
  },
);
